import { areAgentIdListsEqual } from './agent-id-list-equality';

export type SelectedAgentTransitionState = {
	promptKey: string;
	lastHydratedDraftAgentIdsByPromptKey: Readonly<Record<string, readonly string[] | undefined>>;
	isSubmitClearing: boolean;
	isReasoningModeMenuOpen: boolean;
};

export type SelectedAgentTransitionEvent =
	| { type: 'conversationDraftKeyChanged'; promptKey: string }
	| { type: 'draftMentionIdsSeen'; agentIds: readonly string[] }
	| { type: 'submitClearInProgressChanged'; isClearing: boolean }
	| { type: 'reasoningModeMenuOpenChanged'; isOpen: boolean };

const createSelectedAgentTransitionState = (promptKey: string): SelectedAgentTransitionState => ({
	promptKey,
	lastHydratedDraftAgentIdsByPromptKey: {},
	isSubmitClearing: false,
	isReasoningModeMenuOpen: false,
});

const selectedAgentTransitionReducer = (
	state: SelectedAgentTransitionState,
	event: SelectedAgentTransitionEvent,
): SelectedAgentTransitionState => {
	switch (event.type) {
		case 'conversationDraftKeyChanged':
			return event.promptKey === state.promptKey
				? state
				: {
						...state,
						promptKey: event.promptKey,
						isSubmitClearing: false,
						isReasoningModeMenuOpen: false,
					};
		case 'draftMentionIdsSeen':
			return areAgentIdListsEqual(
				state.lastHydratedDraftAgentIdsByPromptKey[state.promptKey],
				event.agentIds,
			)
				? state
				: {
						...state,
						lastHydratedDraftAgentIdsByPromptKey: {
							...state.lastHydratedDraftAgentIdsByPromptKey,
							[state.promptKey]: event.agentIds,
						},
					};
		case 'submitClearInProgressChanged':
			return { ...state, isSubmitClearing: event.isClearing };
		case 'reasoningModeMenuOpenChanged':
			return { ...state, isReasoningModeMenuOpen: event.isOpen };
	}
};

type SelectedAgentTransitionInternals = {
	createState: (promptKey: string) => SelectedAgentTransitionState;
	reduce: (
		state: SelectedAgentTransitionState,
		event: SelectedAgentTransitionEvent,
	) => SelectedAgentTransitionState;
};

/**
 * State/reducer primitives. Consume these only via `useSelectedAgentTransition`,
 * which owns the single ref instance per prompt key; do not create additional
 * refs at call sites or the state machine fragments (see TREX-1616).
 */
export const selectedAgentTransitionInternals: SelectedAgentTransitionInternals = {
	createState: createSelectedAgentTransitionState,
	reduce: selectedAgentTransitionReducer,
};
